import { Equal, Option, Result } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { Rule, makeRules, validate } from "foldkit/fieldValidation";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import { asOfTag } from "../../api/client";
import {
	createAttributeSchema,
	deleteAttributeSchema,
	getAttributeSchema,
	updateAttributeSchema,
} from "../../api/commands/attributeSchema";
import { type ApiError, toMessage } from "../../api/errors";
import {
	type AttributeSchemaEntry,
	normalizeEntityType,
} from "../../api/schemas/attributeSchema";
import { asOfOf } from "../../app/routes";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { type Form, type Model, emptyForm } from "./model";

export const FetchSchema = getAttributeSchema.command(
	Message.CompletedFetchSchema,
	asOfTag,
);
export const CreateEntry = createAttributeSchema.command(
	Message.CompletedCreate,
	({ generation, result }) => Message.CompletedCreate({ generation, result }),
);
export const UpdateEntry = updateAttributeSchema.command(
	Message.CompletedUpdate,
	({ generation, result }) => Message.CompletedUpdate({ generation, result }),
);
export const DeleteEntry = deleteAttributeSchema.command(
	Message.CompletedDelete,
	({ generation, result }) => Message.CompletedDelete({ generation, result }),
);

/** v1's two checks, in order: required (whitespace counts as empty), then the charset. */
export const keyRules = makeRules({
	required: "Key is required",
	isEmpty: (value) => value.trim() === "",
	rules: [
		Rule.pattern(
			/^[a-zA-Z0-9_.-]+$/,
			"Key must contain only alphanumeric characters, underscores, dots, or hyphens",
		),
	],
});

type Return = PageReturn<Model, Message>;

const load = (model: Model, shared: Shared): Return => ({
	model: evo(model, { schema: start }),
	commands: [
		FetchSchema({
			entityType: null,
			asOf: shared.asOf,
			generation: shared.generation,
		}),
	],
});

/** v1 refetches (query invalidation) after every successful mutation. */
const refetch = load;

const editForm = (model: Model, form: (current: Form) => Form): Model =>
	evo(model, { form: (maybeForm) => Option.map(maybeForm, form) });

const orUndefined = (value: string): string | undefined => value || undefined;

const submit = (model: Model, form: Form, shared: Shared): Return => {
	const key = validate(keyRules)(form.key.value);
	if (key._tag !== "Valid") {
		const error =
			key._tag === "Invalid" ? Option.some(key.errors[0]) : Option.none();
		return { model: editForm(model, (f) => ({ ...f, key, error })) };
	}
	const saving = evo(
		editForm(model, (f) => ({ ...f, key, error: Option.none() })),
		{
			saving: () => true,
		},
	);
	const optional = {
		...(form.label ? { label: form.label } : {}),
		...(form.description ? { description: form.description } : {}),
	};
	return Option.match(form.editingId, {
		onSome: (id) => ({
			model: saving,
			commands: [
				UpdateEntry({
					id,
					valueType: form.valueType,
					...optional,
					sortOrder: form.sortOrder,
					generation: shared.generation,
				}),
			],
		}),
		onNone: () => ({
			model: saving,
			commands: [
				CreateEntry({
					entityType: form.entityType,
					key: key.value,
					valueType: form.valueType,
					...optional,
					...(orUndefined(form.subType) ? { subType: form.subType } : {}),
					sortOrder: form.sortOrder,
					generation: shared.generation,
				}),
			],
		}),
	});
};

const saved = (
	model: Model,
	shared: Shared,
	generation: number,
	result: Result.Result<unknown, ApiError>,
): Return => {
	if (generation !== shared.generation) {
		return { model };
	}
	const settled = evo(model, { saving: () => false });
	return Result.match(result, {
		onSuccess: () =>
			refetch(evo(settled, { form: () => Option.none() }), shared),
		onFailure: (error) => ({
			model: editForm(settled, (f) => ({
				...f,
				error: Option.some(toMessage(error)),
			})),
		}),
	});
};

const entries = (model: Model): ReadonlyArray<AttributeSchemaEntry> =>
	AsyncData.getOrElse(model.schema, () => ({ data: [] })).data;

export const page: Page<Model, Message> = {
	init: (_route, shared) =>
		load(
			{
				page: "AttributeSchemaSettings",
				schema: AsyncData.Idle(),
				form: Option.none(),
				saving: false,
			},
			shared,
		),
	changedRoute: (model, previous, next, shared) =>
		Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: load(model, shared),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchSchema: ({ asOf, generation, result }) => ({
				model:
					generation === shared.generation && Equal.equals(asOf, shared.asOf)
						? evo(model, { schema: (schema) => settle(schema, result) })
						: model,
			}),
			ClickedAddEntry: () => ({
				model: evo(model, { form: () => Option.some(emptyForm) }),
			}),
			ClickedEdit: ({ id }) => {
				const entry = entries(model).find((e) => e.id === id);
				return {
					model: entry
						? evo(model, {
								form: () =>
									Option.some<Form>({
										editingId: Option.some(entry.id),
										entityType: normalizeEntityType(entry.entityType),
										key: { _tag: "NotValidated", value: entry.key },
										valueType: entry.valueType,
										label: entry.label ?? "",
										description: entry.description ?? "",
										subType: entry.subType ?? "",
										sortOrder: entry.sortOrder,
										error: Option.none(),
									}),
							})
						: model,
				};
			},
			// v1 deletes immediately, no confirmation; a failure is console-only.
			ClickedDelete: ({ id }) => ({
				model,
				commands: [DeleteEntry({ id, generation: shared.generation })],
			}),
			ClickedCancel: () => ({
				model: evo(model, { form: () => Option.none() }),
			}),
			ChangedEntityType: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, entityType: value })),
			}),
			ChangedKey: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					key: { _tag: "NotValidated", value },
				})),
			}),
			ChangedValueType: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, valueType: value })),
			}),
			ChangedLabel: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, label: value })),
			}),
			ChangedDescription: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, description: value })),
			}),
			ChangedSubType: ({ value }) => ({
				model: editForm(model, (f) => ({ ...f, subType: value })),
			}),
			// v1: `Number.parseInt(value, 10) || 0`.
			ChangedSortOrder: ({ value }) => ({
				model: editForm(model, (f) => ({
					...f,
					sortOrder: Number.parseInt(value, 10) || 0,
				})),
			}),
			SubmittedForm: () =>
				Option.match(model.form, {
					onNone: () => ({ model }),
					onSome: (form) =>
						model.saving ? { model } : submit(model, form, shared),
				}),
			CompletedCreate: ({ generation, result }) =>
				saved(model, shared, generation, result),
			CompletedUpdate: ({ generation, result }) =>
				saved(model, shared, generation, result),
			CompletedDelete: ({ generation, result }) =>
				generation === shared.generation && Result.isSuccess(result)
					? refetch(model, shared)
					: { model },
		}),
};
